/**
 * <chart-north> — North Indian kundali: a square with fixed bhāvas and moving
 * rāśis. Bhāva 1 is the top diamond. SVG, no canvas, so it prints and scales.
 *
 * ⚠ **Bhāva-fixed.** The top diamond is always bhāva 1; the *rāśi number* inside
 * it changes from chart to chart. the classical Figure 1 makes the point explicitly. A
 * renderer that put Meṣa in a fixed box here would be drawing a South Indian
 * chart with North Indian lines.
 *
 * ◆ Light DOM, not shadow. The chart must be selectable text, must inherit the
 * page's print stylesheet, and must be reachable by a user's own browser
 * find-in-page. Shadow DOM would cost all three for encapsulation this component
 * does not need.
 *
 * © Manish Jagdish Thatte
 */

import {
  NORTH_CELLS, NORTH_LABEL_ANCHORS, NORTH_LABEL_MAX_WIDTH, NORTH_LINES,
  NORTH_NUMBER_ANCHORS, el, polygonPath, renderCellLabels
} from '../utils/svg.js';
import { graha } from '../jyotish/constants.js';
import { localName } from '../utils/i18n.js';

export class ChartNorth extends HTMLElement {
  static observedAttributes = ['locale', 'labels'];

  connectedCallback() { this.render(); }
  attributeChangedCallback() { if (this.isConnected) this.render(); }

  /** `{ bhavas, grahas, lagna }` — the shape `generateKundali` returns. */
  set kundali(value) { this._kundali = value; this.render(); }
  get kundali() { return this._kundali ?? null; }

  render() {
    const k = this._kundali;
    this.textContent = '';
    if (!k) return;

    const locale = this.getAttribute('locale') || 'en';
    const useSymbols = this.getAttribute('labels') === 'symbols';

    // ⚠ Without bhāvas there is no North Indian chart to draw — the whole
    // geometry is houses. Say so rather than drawing an empty square, which
    // reads as a rendering failure.
    if (!k.bhavas) {
      this.append(noBhavaNote());
      return;
    }

    const svg = el('svg', {
      viewBox: '-2 -2 104 104',
      class: 'chart chart--north',
      role: 'img',
      'aria-label': `North Indian chart, lagna in ${localName(k.lagna.rashi.name, locale)}`
    });

    const lines = el('g', { class: 'chart__lines' });
    for (const [[x1, y1], [x2, y2]] of NORTH_LINES) {
      lines.append(el('line', { x1, y1, x2, y2 }));
    }
    svg.append(lines);

    // ⚠ The <svg> goes into the document BEFORE the cells are filled.
    // renderCellLabels measures with getComputedTextLength(), which returns 0
    // for a detached node — so a chart built entirely off-document would report
    // that everything fits and then overflow on screen.
    this.append(svg);

    const byBhava = k.occupancy?.byBhava ?? {};

    for (let i = 0; i < 12; i++) {
      const number = i + 1;
      const cell = NORTH_CELLS[i];
      const bhava = k.bhavas[i];
      const g = el('g', { class: 'chart__cell', 'data-bhava': number });

      // An invisible hit area, so a tap anywhere in the cell selects the bhāva.
      g.append(el('path', { d: polygonPath(cell), class: 'chart__cell-area' }));
      svg.append(g);

      // ◆ The rāśi NUMBER, which is what a North Indian chart carries in place
      // of a rāśi name — the classical Figure 1 prints "4" for Karka.
      const [nx, ny] = NORTH_NUMBER_ANCHORS[i];
      g.append(el('text', {
        x: nx, y: ny, class: 'chart__rashi-number', 'text-anchor': 'middle',
        'dominant-baseline': 'middle'
      }, String(bhava.rashiIndex + 1)));

      const occupants = byBhava[number] ?? [];
      if (occupants.length) {
        renderCellLabels(g, occupants.map((id) => localName(graha(id).name, locale)), {
          cell,
          anchor: NORTH_LABEL_ANCHORS[i],
          maxWidth: NORTH_LABEL_MAX_WIDTH[i],
          makeSpan: grahaSpan(k, occupants, locale),
          abbreviate: (_, idx) => graha(occupants[idx] ?? occupants[0]).name.symbol,
          baseFont: useSymbols ? 4.4 : 4.8
        });
      }

      if (number === 1) g.classList.add('is-lagna');
    }
  }
}

/** One graha's <tspan>, with its state classes and a tooltip when abbreviated. */
function grahaSpan(kundali, occupants, locale) {
  return (label, index, abbreviated) => {
    const id = occupants[index] ?? occupants[0];
    const info = kundali.grahas[id];
    const span = el('tspan', {
      class: [
        'chart__graha',
        info?.retrograde ? 'is-retrograde' : null,
        info?.combust ? 'is-combust' : null
      ].filter(Boolean).join(' '),
      'data-graha': id
    }, label + (info?.retrograde ? '\u2009\u211E' : ''));
    // ⚠ The full name goes in a tooltip whenever the cell abbreviated, so the
    // two-letter form is never the only thing available.
    if (abbreviated) span.append(el('title', {}, localName(graha(id).name, locale)));
    return span;
  };
}

function noBhavaNote() {
  const p = document.createElement('p');
  p.className = 'chart__no-bhavas';
  p.textContent =
    'A North Indian chart is drawn from the bhāvas, and this chart has none — ' +
    'the birth time is not known. The rāśi chart styles still show every graha.';
  return p;
}

customElements.define('chart-north', ChartNorth);
